<script setup lang="ts">
import { computed, onMounted, onUnmounted, ref } from 'vue'
import { Button, Icon, ToggleSwitch, Tooltip } from '@easybell/caramel-vue3'
import { useFeaturesStore } from '@/stores/features/store'

defineProps<{
  isHelpHiglighted: boolean
}>()

const emit = defineEmits<{
  (e: 'openUcaWelcomeModal'): void
}>()

const featuresStore = useFeaturesStore()

const isHelpTooltipOpen = ref(false)

const helpMenuRef = ref<HTMLElement | null>(null)

const hasHelpVisible = computed({
  get() {
    return featuresStore.areHelpTipsVisible
  },
  set() {
    featuresStore.toggleAreHelpTipsVisible()
  },
})

function onDocumentClick(event: MouseEvent) {
  if (helpMenuRef.value && !helpMenuRef.value.contains(event.target as Node)) {
    isHelpTooltipOpen.value = false
  }
}

onMounted(() => document.addEventListener('click', onDocumentClick))
onUnmounted(() => document.removeEventListener('click', onDocumentClick))

function openOnboarding() {
  isHelpTooltipOpen.value = false
  emit('openUcaWelcomeModal')
}

function openHelpCenter() {
  isHelpTooltipOpen.value = false
  window.open(
    'https://www.easybell.de/hilfe/cloud-telefonanlage/',
    '_blank',
    'noopener,noreferrer'
  )
}
</script>

<template>
  <div ref="helpMenuRef" class="fixed bottom-20 right-6 z-40">
    <Tooltip
      v-model="isHelpTooltipOpen"
      toggle-on-click
      position="top"
      shift="left"
    >
      <template #target>
        <Button
          leading-icon="QuestionInCircle"
          :round="true"
          data-test="uca-floating-button"
          :class="{
            'tutorial-highlight after:!rounded-full': isHelpHiglighted,
          }"
        >
          {{ $t('footer.pages.title.help') }}
        </Button>
      </template>

      <template #tooltip>
        <div class="flex flex-col">
          <label
            class="px-4 h-9 hover:bg-neutral-100 cursor-pointer flex items-center gap-2"
          >
            <Icon name="LampSpark" class="size-4" />

            <div>{{ $t('footer.help.tips') }}</div>

            <ToggleSwitch
              v-model="hasHelpVisible"
              data-test="help-menu-switch"
              class="ml-auto"
            />
          </label>

          <button
            class="px-4 py-2 text-left text-sm hover:bg-neutral-100 flex items-center gap-2"
            data-test="help-menu-onboarding"
            @click="openOnboarding"
          >
            <Icon name="PlayMedia" class="size-4 shrink-0" />
            {{ $t('general.help.onboarding.uca') }}
          </button>

          <button
            class="px-4 py-2 text-left text-sm hover:bg-neutral-100 flex items-center gap-2"
            data-test="help-menu-documentation"
            @click="openHelpCenter"
          >
            <Icon name="DocumentFileRounded" class="size-4 shrink-0" />
            {{ $t('menu.documentation') }}
          </button>
        </div>
      </template>
    </Tooltip>
  </div>
</template>
